import { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, Shirt, LayoutGrid, Image, Megaphone, BarChart3,
  Wallet, Users, UserCog, Layout, Settings, LogOut, Store, LineChart, ImagePlus, PenLine, Star, QrCode, LifeBuoy, HelpCircle, BookOpen, Palette, Activity,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/store/Logo";

const S = "super_admin", M = "store_manager", F = "fulfillment_staff";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: [S, M, F], end: true },
  { to: "/admin/orders", label: "Orders", icon: Package, roles: [S, M, F] },
  { to: "/admin/products", label: "Products", icon: Shirt, roles: [S, M] },
  { to: "/admin/categories", label: "Categories", icon: LayoutGrid, roles: [S, M] },
  { to: "/admin/media", label: "Media", icon: Image, roles: [S, M] },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone, roles: [S, M] },
  { to: "/admin/qr-labels", label: "QR Labels", icon: QrCode, roles: [S, M] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: [S, M] },
  { to: "/admin/analytics", label: "Analytics", icon: LineChart, roles: [S, M] },
  { section: "Agency Control", roles: [S] },
  { to: "/admin/agency-fees", label: "Agency Fees", icon: Wallet, roles: [S] },
  { to: "/admin/affiliates", label: "Affiliates", icon: Users, roles: [S] },
  { to: "/admin/users", label: "Users & Roles", icon: UserCog, roles: [S] },
  { to: "/admin/blog", label: "Blog / Journal", icon: PenLine, roles: [S, M] },
  { to: "/admin/reviews", label: "Reviews", icon: Star, roles: [S, M] },
  { to: "/admin/cms", label: "CMS Builder", icon: Layout, roles: [S] },
  { to: "/admin/our-story", label: "Our Story", icon: BookOpen, roles: [S] },
  { to: "/admin/brand", label: "Brand Kit", icon: Palette, roles: [S] },
  { to: "/admin/page-images", label: "Page Images", icon: ImagePlus, roles: [S] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: [S] },
  { to: "/admin/activity", label: "Team Activity", icon: Activity, roles: [S] },
];

const ROLE_LABEL = { super_admin: "Agency · Super Admin", store_manager: "Store Manager", fulfillment_staff: "Fulfillment Staff" };

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (ready && (!user || !["super_admin", "store_manager", "fulfillment_staff"].includes(user.role)))
      navigate("/login", { state: { from: "/admin" } });
  }, [ready, user]); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    const beat = () => api.post("/admin/activity/heartbeat", { path: location.pathname }).catch(() => {});
    beat();
    const id = setInterval(beat, 30000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  if (!ready || !user) return <div className="min-h-screen flex items-center justify-center font-heading text-3xl text-ink/40">Loading…</div>;

  const visible = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-[#f9f6f0]">
      <aside className="w-60 bg-cream border-r border-ink/10 flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-ink/10"><Logo /></div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-4 px-3 space-y-1">
          {visible.map((n, i) =>
            n.section ? (
              <p key={i} className="font-body uppercase tracking-luxe text-[0.55rem] text-ink/40 px-3 pt-5 pb-2">{n.section}</p>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`adminnav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 font-body text-[0.72rem] tracking-wide2 transition-colors ${isActive ? "bg-ink text-linen" : "text-ink/70 hover:bg-warm/40"}`}>
                <n.icon size={16} strokeWidth={1.5} /> {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="border-t border-ink/10 p-3 space-y-1">
          <NavLink to="/admin/guide" data-testid="adminnav-help-guide"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 font-body text-[0.72rem] tracking-wide2 transition-colors ${isActive ? "bg-ink text-linen" : "text-ink/70 hover:bg-warm/40"}`}>
            <LifeBuoy size={16} strokeWidth={1.5} /> Help / Guide
          </NavLink>
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 font-body text-[0.72rem] tracking-wide2 text-ink/70 hover:bg-warm/40"><Store size={16} strokeWidth={1.5} /> View Store</Link>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 font-body text-[0.72rem] tracking-wide2 text-ink/70 hover:bg-warm/40"><LogOut size={16} strokeWidth={1.5} /> Sign Out</button>
        </div>
      </aside>

      <div className="flex-1 ml-60">
        <header className="bg-linen/80 backdrop-blur-md border-b border-ink/10 px-8 h-16 flex items-center justify-between sticky top-0 z-20">
          <p className="font-body text-[0.7rem] uppercase tracking-wide2 text-ink/60">Admin Console</p>
          <div className="flex items-center gap-3">
            {!location.pathname.startsWith("/admin/guide") && (
              <Link to={`/admin/guide?from=${encodeURIComponent(location.pathname)}`}
                title="How to use this page"
                data-testid="admin-help-button"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-ink/20 text-ink/55 hover:bg-ink hover:text-linen hover:border-ink transition-colors">
                <HelpCircle size={16} strokeWidth={1.5} />
              </Link>
            )}
            <span className="font-body text-[0.62rem] uppercase tracking-wide2 bg-warm/50 px-3 py-1" data-testid="admin-role-badge">{ROLE_LABEL[user.role]}</span>
            <span className="font-body text-sm flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Online" data-testid="admin-self-online" />{user.name}</span>
          </div>
        </header>
        <main className="p-8"><Outlet /></main>
      </div>
    </div>
  );
}
